import { zodResolver } from '@hookform/resolvers/zod';
import { useGetLabels } from '@ipfabric/api-client/client/model/labels/labels';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { TooltipIcon } from '@ipfabric/ui-components/Tooltip/TooltipIcon';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import { useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';
import {
  type PolicyFormMode,
  canSaveInMode,
} from '../../shared/policyFormMode';
import { useWriteScopes } from '../../shared/useWriteScopes';
import { EnrolmentPanel } from '../ManageEnrolledLabels/ManageEnrolledLabelsPanel';
import { type DataPolicy, useDataPoliciesApi } from '../dataPoliciesApi';
import { DataPolicyConditionEditor } from './DataPolicyConditionEditor';
import { GuardedEntityCounts } from './GuardedEntityCounts';
import {
  type DataPolicyFormValues,
  dataPolicyFormSchema,
  toCondition,
  toFormValues,
} from './dataPolicyFormSchema';
import { useImpactPreview } from './useImpactPreview';
import { usePruneUnenrolledTerms } from './usePruneUnenrolledTerms';

export type DataPolicyFormMode = PolicyFormMode;

type DataPolicyFormProps = {
  mode: DataPolicyFormMode;
  policy?: DataPolicy;
  onClose: () => void;
};

const TITLE_BY_MODE: Record<DataPolicyFormMode, string> = {
  add: 'New data policy',
  edit: 'Edit data policy',
  duplicate: 'New data policy',
  view: 'Data policy',
};

export const DataPolicyForm = ({
  mode,
  policy,
  onClose,
}: DataPolicyFormProps) => {
  const [view, setView] = useState<'main' | 'labels'>('main');
  const snapshotId = useSnapshotId();
  const scopes = useWriteScopes('/data-policies', '/data-policies/:id');
  // Without the write scope for this mode the panel only shows the record.
  const readOnly = !canSaveInMode(mode, scopes);
  const { showNotification } = useNotifications();
  const { createDataPolicy, updateDataPolicy } = useDataPoliciesApi();
  const labelsQuery = useGetLabels();

  const form = useCustomForm<DataPolicyFormValues>({
    defaultValues: toFormValues(
      policy,
      mode === 'duplicate' && policy ? `Copy of ${policy.name}` : undefined,
    ),
    resolver: zodResolver(dataPolicyFormSchema),
  });
  const { control, formState, handleSubmit } = form;
  const terms = useWatch({ control, name: 'terms' }) ?? [];
  const preview = useImpactPreview(snapshotId, terms);

  const enrolledLabels = (labelsQuery.data ?? []).filter(
    (label) => label.rbacEnrolled,
  );

  usePruneUnenrolledTerms({
    form,
    enrolledLabels,
    labelsLoaded: labelsQuery.status === 'success',
    enabled: !readOnly,
  });

  const onSubmit = handleSubmit(async (values) => {
    const body = { name: values.name, condition: toCondition(values.terms) };
    if (mode === 'edit' && policy) {
      await updateDataPolicy(policy.id, body);
    } else {
      await createDataPolicy(body);
    }
    showNotification({
      type: 'success',
      message: mode === 'edit' ? 'Data policy updated' : 'Data policy created',
    });
    onClose();
  });

  if (view === 'labels') {
    const backToMain = () => setView('main');
    return (
      <EnrolmentPanel
        onClose={onClose}
        onBack={backToMain}
        onCancel={backToMain}
        onDone={backToMain}
      />
    );
  }

  const termsError =
    formState.errors.terms?.message ?? formState.errors.terms?.root?.message;

  const footer = (
    <div className='flex gap-xs'>
      {!readOnly && (
        <Button
          className='flex-1'
          dataTestId='data-policy-save'
          icon='fa-floppy-disk'
          loading={formState.isSubmitting}
          onClick={() => void onSubmit()}
          type='button'
          variant='primary'
        >
          Save
        </Button>
      )}
      <Button
        className='flex-1'
        dataTestId='data-policy-close'
        onClick={onClose}
        type='button'
        variant='outline'
      >
        Close
      </Button>
    </div>
  );

  return (
    <SidePanel
      title={TITLE_BY_MODE[mode]}
      closeOnBackdropClick={false}
      onClose={onClose}
      footer={footer}
    >
      <form className='flex flex-col gap-sm' onSubmit={onSubmit}>
        <Card className='flex flex-col gap p-sm'>
          <div className='flex items-center justify-between border-b border-default pb-xs'>
            <span className='inline-flex items-center gap-3xs text-sm font-semibold'>
              Enrolled labels: {enrolledLabels.length}
              <TooltipIcon content='Only labels enrolled for RBAC can be used in data policy conditions.' />
            </span>
            {!readOnly && (
              <Button
                dataTestId='data-policy-manage-enrolled-labels'
                disabled={snapshotId === null}
                icon='fa-angle-right'
                iconPosition='end'
                onClick={() => setView('labels')}
                type='button'
                variant='plain'
              >
                Manage enrolled labels
              </Button>
            )}
          </div>
          <FormTextField
            control={control}
            dataTestId='data-policy-name'
            disabled={readOnly}
            label='Policy name'
            name='name'
            type='text'
          />
          <DataPolicyConditionEditor
            control={control}
            disabled={readOnly}
            enrolledLabels={enrolledLabels}
            error={termsError}
          />
        </Card>
        <GuardedEntityCounts
          preview={preview}
          snapshotSelected={snapshotId !== null}
        />
      </form>
    </SidePanel>
  );
};
